import { reactive } from 'vue';
import { useApi } from '~/composables/useApi';

const DEFAULT_MAX_FILE_SIZE = 52428800;
const MAX_TOTAL_SIZE_STORAGE_KEY = 'maxTotalSize';

/** compId별 첨부 총 용량 — loginProcess.do maxTotalSize 로 갱신 */
const attachmentConfig = reactive({
  maxFileCnt: 5,
  maxFileSize: DEFAULT_MAX_FILE_SIZE,
  allowFileExts: [
      'jpg',
      'jpeg',
      'gif',
      'png',
      'bmp',
      'txt',
      'pdf',
      'xls',
      'xlsx',
      'ppt',
      'pptx',
      'doc',
      'docx',
      'hwp',
      'zip',
      '7z',
      'rar',
      'alz',
      'hwpx',
      'heic',
      'avi',
      'mp4',
    ],
  atchFileColumnNames: ['atchFileId'],
});

const persistMaxFileSizeToStorage = (value: number) => {
  if (!import.meta.client) return;
  try {
    localStorage.setItem(MAX_TOTAL_SIZE_STORAGE_KEY, String(value));
  } catch {
    // storage 접근 불가 시 무시
  }
};

const loadMaxFileSizeFromStorage = (): number => {
  if (!import.meta.client) return 0;
  try {
    const saved = Number(localStorage.getItem(MAX_TOTAL_SIZE_STORAGE_KEY) ?? '');
    return Number.isFinite(saved) && saved > 0 ? saved : 0;
  } catch {
    return 0;
  }
};

const removeMaxFileSizeFromStorage = () => {
  if (!import.meta.client) return;
  try {
    localStorage.removeItem(MAX_TOTAL_SIZE_STORAGE_KEY);
  } catch {
    // storage 접근 불가 시 무시
  }
};

/** 로그인 JSON 응답 maxTotalSize 반영 */
export const setAttachmentMaxFileSize = (value: unknown) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return;

  attachmentConfig.maxFileSize = parsed;
  persistMaxFileSizeToStorage(parsed);
};

/** 새로고침·세션 복구 시 localStorage에서 복원 */
export const restoreAttachmentMaxFileSizeFromStorage = () => {
  const saved = loadMaxFileSizeFromStorage();
  if (saved > 0) {
    attachmentConfig.maxFileSize = saved;
    return true;
  }
  return false;
};

/** 로그아웃 시 초기화 */
export const clearAttachmentMaxFileSize = () => {
  attachmentConfig.maxFileSize = DEFAULT_MAX_FILE_SIZE;
  removeMaxFileSizeFromStorage();
};

export const useAttachmentApi = () => {
  // file 부가정보
  type FileMeta = {
    // 타입에 따른 설정 ( notice, report .... )
    inputFileNames?: string[];
    atchFileIds?: string[];
    /** 지정 시 atchFileValidations 한 건만 덮어씀(예: 화면별 maxFileCnt 상이) */
    atchFileValidations?: string;
  };

  // files, deleteFileIds, fileMeta => formData 삽입
  const appendFileToFormData = ({
    formData,
    files,
    deleteFileIds = [],
    fileMeta = {},
  }: {
    formData: FormData;
    files: File[]; // 신규 업로드 파일들
    deleteFileIds?: (string | number)[]; // 삭제할 fileSn 목록
    fileMeta?: FileMeta; // inputFileNames, atchFileIds 등 메타정보
  }) => {
    const key = fileMeta.inputFileNames?.[0] ?? 'files';

    // 1) 신규 파일 추가 (파일 여러 개면 append가 여러 번 호출됨)
    files.forEach(file => {
      formData.append(key, file);
    });

    // 2) 삭제 대상 fileSn 목록 추가
    const joined = deleteFileIds.map(String).join(',');
    formData.append('chkAttachFiles', joined);
    formData.append(`${key}_chkAttachFiles`, joined);

    // 3) inputFileNames (단일 값)
    if (fileMeta.inputFileNames?.[0]) {
      formData.append('inputFileNames', fileMeta.inputFileNames[0]);
    }

    // 4) atchFileIds (단일 값)
    if (fileMeta.atchFileIds?.[0]) {
      formData.append('atchFileIds', fileMeta.atchFileIds[0]);
    } else {
      formData.append('atchFileIds', 'NULL');
    }

    // 5) 고정 설정 (단일 값)
    formData.append('atchFileColumnNames', attachmentConfig.atchFileColumnNames[0]);
    if (fileMeta.atchFileValidations) {
      formData.append('atchFileValidations', fileMeta.atchFileValidations);
    } else {
      formData.append(
        'atchFileValidations',
        `${attachmentConfig.maxFileCnt}_${attachmentConfig.maxFileSize}_${attachmentConfig.allowFileExts.join(',')}`,
      );
    }
  };

  // 카테고리 별 첨부파일 업로드 처리
  const uploadWithDataAndFiles = (url: string, formData: FormData) => {
    return useApi(url, {
      method: 'POST',
      body: formData,
      headers: {
        // formData 보내기 위해 useApi 의 고정 Content-Type 제거
        Accept: 'application/json',
      },
    });
  };

  // 첨부파일 목록 조회 (CommonController getFileList — @RequestParam atchFileId, 레거시 initAttach와 동일하게 폼 본문)
  const fetchAtchFileList = (param: { atchFileId: string }) => {
    return useApi('/api/common/getFileList.do', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({ atchFileId: String(param.atchFileId) }).toString(),
    });
  };

  // 첨부파일 목록 조회(인증전)
  const fetchPublicAtchFileList = (param: { atchFileId: String }) => {
    return useApi('/api/common/selectNoticeFileListForAll_json.do', {
      method: 'POST',
      query: param,
      skipSessionExpiredCheck: true, // 로그인 페이지 공개 공지 첨부 API (인증전)
    });
  };

  // 첨부파일 다운로드
  const fetchAtchFileDownload = async (param: { atchFileId: string; fileSn: number }) => {
    const config = useRuntimeConfig();
    const isDev = config.public.isDev;
    const apiUrl = isDev ? '/api/cmm/fms/FileDown.do' : '/cmm/fms/FileDown.do';

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        atchFileId: param.atchFileId,
        fileSn: param.fileSn.toString(), // 🔹 number → string 변환
      }),
    });

    if (!response.ok) {
      throw new Error('파일 다운로드에 실패했습니다.');
    }

    // Blob 객체로 읽어 브라우저에서 처리가능하게 함
    const blob = await response.blob();

    // 파일명 추출
    let filename = 'downloaded.file';
    const disposition = response.headers.get('Content-Disposition');
    // 헤더가 존재할 시 filename 을 응답 헤더에서 추출하여 덮어줌
    if (disposition) {
      const utf8NameMatch = disposition.match(/filename\*=UTF-8''(.+)$/);
      const plainNameMatch = disposition.match(/filename="?([^"]+)"?/);

      if (utf8NameMatch) {
        // UTF-8 방식
        filename = decodeURIComponent(utf8NameMatch[1]);
      } else if (plainNameMatch) {
        // 일반 ASCII 방식
        filename = plainNameMatch[1];
      }
    }

    /** 다운로드 실행 */
    // Blob을 브라우저 메모리에 임시로 올려 URL 생성
    const blobUrl = window.URL.createObjectURL(blob);
    // 가상의 <a> 태그 형성
    const link = document.createElement('a');
    link.href = blobUrl;
    // 다운로드할 때 사용할 파일 이름을 지정
    link.download = filename;
    // 생성한 <a> 태그를 실제 DOM(문서)에 추가
    document.body.appendChild(link);
    // 파일 다운로드를 트리거
    link.click();
    // 추가한 <a> 태그를 DOM 에서 제거
    link.remove();
    // createObjectURL()로 만든 blob URL을 메모리에서 해제
    window.URL.revokeObjectURL(blobUrl);
  };

  return {
    attachmentConfig,
    appendFileToFormData,
    uploadWithDataAndFiles,
    fetchAtchFileList,
    fetchPublicAtchFileList,
    fetchAtchFileDownload,
  };
};
